import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import castArray from "lodash/castArray";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";
import {
  getVarsoUrl,
  getAlleleFrequency,
  renderMainDataPoint,
} from "../../../../common/helpers/helpers";
import { mitomapListSelector } from "../../../../state/selectors/Mitomap.selectors";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import { getPathogenicityColorByAllNames } from "../../../../common/helpers/Constant";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperText from "../../../../components/atoms/HelperText";

class Mitomap extends React.Component {
  calcDiseaseColor = (diseaseStatus) => {
    let color = getPathogenicityColorByAllNames("VUS");
    if (diseaseStatus) {
      const status = diseaseStatus;

      // Check for explicit classifications, order is important as P* matches LP* too.
      if (status.includes("LP*")) {
        color = getPathogenicityColorByAllNames("Likely Pathogenic");
      } else if (status.includes("P*")) {
        color = getPathogenicityColorByAllNames("Pathogenic");
      } else if (status.includes("VUS")) {
        color = getPathogenicityColorByAllNames("VUS");
      } else if (status.includes("B*")) {
        color = getPathogenicityColorByAllNames("Benign");
      }

      // Check the first word of the status:
      else if (status.startsWith("Cfrm")) {
        color = getPathogenicityColorByAllNames("Pathogenic");
      } else if (status.startsWith("Reported")) {
        color = getPathogenicityColorByAllNames("Likely Pathogenic");
      } else if (status.startsWith("Unclear")) {
        color = getPathogenicityColorByAllNames("VUS");
      } else if (status.startsWith("Conflicting")) {
        color = getPathogenicityColorByAllNames("VUS");
      }
    }
    return <span style={{ color, fontWeight: "bold" }}>{diseaseStatus}</span>;
  };

  calcFrequencyColor = (frequency) => {
    if (!frequency) {
      return null;
    }
    const value = parseFloat(frequency.substring(4));
    let color = "black";
    if (value === 0) {
      color = getPathogenicityColorByAllNames("Likely Pathogenic");
    } else if (value < 0.01) {
      color = getPathogenicityColorByAllNames("Uncertain Significance");
    } else if (value >= 0.01 && value < 0.05) {
      color = getPathogenicityColorByAllNames("Likely Benign");
    } else if (value >= 0.05) {
      color = getPathogenicityColorByAllNames("Benign");
    }

    return <span style={{ color }}>{frequency}</span>;
  };

  getTableMobile = (item) => {
    const diseases = castArray(item.diseases || []).join(",") || "-";
    const het =
      (item.heteroplasmy && (item.heteroplasmy === "+" ? "Yes" : "No")) || "-";
    const hom =
      (item.homoplasmy && (item.homoplasmy === "+" ? "Yes" : "No")) || "-";

    const tableMobile = (
      <div>
        <dl className="dl-horizontal varsome-dl-horizontal">
          <dt className="col-9 col-xs-9">
            {"Allele Count "}
            <HelperText fieldDescriptionKey="Mitomap Allele Count" />
          </dt>
          <dd className="col-3 col-xs-3">{item.ac ? item.ac : "-"}</dd>
          <dt className="col-9 col-xs-9">Allele Frequency</dt>
          <dd className="col-3 col-xs-3">
            {item.af ? getAlleleFrequency(item.af, 4) : "-"}
          </dd>
          <dt className="col-9 col-xs-9">Disease status</dt>
          <dd className="col-3 col-xs-3">
            {item.disease_status ? item.disease_status : "-"}
          </dd>
          <dt className="col-9 col-xs-9">Diseases</dt>
          <dd className="col-12 col-xs-12">{diseases}</dd>
          <dt className="col-9 col-xs-9">Heteroplasmy</dt>
          <dd className="col-3 col-xs-3">{het}</dd>
          <dt className="col-9 col-xs-9">Homoplasmy</dt>
          <dd className="col-3 col-xs-3">{hom}</dd>
          <dt className="col-9 col-xs-9">Publications</dt>
          <dd className="col-3 col-xs-3">
            {item.pub_med_references ? (
              <PublicationLinks ids={item.pub_med_references} />
            ) : (
              "-"
            )}
          </dd>
        </dl>
      </div>
    );
    return tableMobile;
  };

  getTableHeaders = () => {
    return (
      <thead>
        <tr>
          <th>
            {"Allele Count "}
            <HelperText fieldDescriptionKey="Mitomap Allele Count" />
          </th>
          <th>Allele Frequency</th>
          <th>Disease status</th>
          <th>Diseases</th>
          <th>Heteroplasmy</th>
          <th>Homoplasmy</th>
          <th>Publications</th>
        </tr>
      </thead>
    );
  };

  getTableRow = (item) => {
    const diseases = castArray(item.diseases || []).join(",") || "-";
    const het =
      (item.heteroplasmy && (item.heteroplasmy === "+" ? "Yes" : "No")) || "-";
    const hom =
      (item.homoplasmy && (item.homoplasmy === "+" ? "Yes" : "No")) || "-";
    const row = (
      <tr>
        <td>{item.ac ? item.ac : "-"}</td>
        <td>{item.af ? getAlleleFrequency(item.af, 4) : "-"}</td>
        <td>
          {item.disease_status
            ? this.calcDiseaseColor(item.disease_status)
            : "-"}
        </td>
        <td>{diseases}</td>
        <td>{het}</td>
        <td>{hom}</td>
        <td>
          {item.pub_med_references ? (
            <PublicationLinks ids={item.pub_med_references} />
          ) : (
            "-"
          )}
        </td>
      </tr>
    );
    return row;
  };

  render() {
    const { mitomap } = this.props;
    if (mitomap) {
      const { version } = mitomap[0];
      const titleDetails = this.calcFrequencyColor(mitomap[0]?.main_data);
      const message = <small>Data lifted-over from chromosome MT</small>;
      const url = getVarsoUrl(
        "https://mitomap.org/foswiki/bin/view////Main/SearchAllele"
      );

      const tmpl = mitomap.map((item) => {
        return (
          <div key={item.id} className="varsome-inner-container">
            {item && (
              <div>
                <MediaQuery minWidth={768}>
                  <table className="varsome-table tw-mt-4">
                    {this.getTableHeaders()}
                    <tbody>{this.getTableRow(item)}</tbody>
                  </table>
                </MediaQuery>
                <MediaQuery maxWidth={767}>
                  {this.getTableMobile(item)}
                </MediaQuery>
              </div>
            )}
          </div>
        );
      });
      return (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              version={version}
              url={url}
              titleDetailsRight={message}
              helperPopup={<HelperText fieldDescriptionKey="Mitomap" />}
              titleDetails={renderMainDataPoint(titleDetails)}
              content={tmpl}
              title="Mitomap"
            />
          </div>
        </div>
      );
    }

    return null;
  }
}

Mitomap.propTypes = {
  mitomap: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      main_data: PropTypes.string,
    })
  ),
};

Mitomap.defaultProps = {
  mitomap: null,
};

const mapStateToProps = (state) => {
  return {
    mitomap: mitomapListSelector(state),
  };
};

const mapDispatchToProps = {};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorGuard(Mitomap, "Mitomap", "Mitomap"));
